import { useState, useEffect, useRef } from 'react';
import '../styles/ActionButtons.css';
import './CheckoutModal.css';
import { fetchWithTimeout } from '../utils/fetchWithTimeout';
import { stripHtml } from '../utils/sanitizeHtml';

const CheckoutModal = ({
  isOpen,
  onClose,
  cartItems: initialCartItems,
  onCartUpdate,
  sessionId,
  apiBaseUrl,
  token,
  customerDetails,
}) => {
  const [cartItems, setCartItems] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [customer, setCustomer] = useState({ name: '', phone: '', email: '' });
  const modalOpenedAtRef = useRef(Date.now());
  const placeOrderLockRef = useRef(false);
  const checkoutOpenedRef = useRef(false);
  const trackingSubmittedRef = useRef(false);

  const resetLocalFormState = () => {
    setIsSubmitting(false);
    setCustomer({ name: '', phone: '', email: '' });
    placeOrderLockRef.current = false;
    checkoutOpenedRef.current = false;
    trackingSubmittedRef.current = false;
  };

  useEffect(() => {
    if (isOpen && initialCartItems) {
      modalOpenedAtRef.current = Date.now();
      placeOrderLockRef.current = false;
      checkoutOpenedRef.current = false;
      trackingSubmittedRef.current = false;
      setIsSubmitting(false);
      setCartItems(initialCartItems);
    } else if (!isOpen) {
      resetLocalFormState();
    }
  }, [isOpen, initialCartItems]);

  useEffect(() => {
    setCustomer({
      name: (customerDetails?.name || '').toString().trim(),
      phone: (customerDetails?.phone || '').toString().trim(),
      email: (customerDetails?.email || '').toString().trim(),
    });
  }, [customerDetails, isOpen]);

  // Parse price string (e.g., "₹29.99" or "29.99") to number
  const parsePrice = (priceString) => {
    if (!priceString) return 0;
    const cleaned = priceString.toString().replace(/[^0-9.]/g, '');
    return parseFloat(cleaned) || 0;
  };

  const _toNumberPrice = (v) => {
    if (v == null || v === '') return 0;
    if (typeof v === 'number') return Number(v);
    return parsePrice(v);
  };

  const getUnitPriceNumber = (product) => {
    if (!product || typeof product !== 'object') return 0;
    const p = product;
    const sale = p.sale_price ?? p.salePrice ?? p.net_price ?? p.netPrice ?? null;
    const primary = sale != null ? sale : (p.sale_price === 0 ? 0 : null);
    if (primary != null) return _toNumberPrice(primary);
    // Fallback order: price, amount, mrp
    return _toNumberPrice(p.price ?? p.amount ?? p.mrp ?? p.selling_price ?? 0);
  };

  // Calculate individual item total (price * quantity)
  const getItemTotal = (item) => {
    const price = getUnitPriceNumber(item.product);
    return price * item.quantity;
  };

  // Calculate grand total
  const getGrandTotal = () => {
    return cartItems.reduce((total, item) => total + getItemTotal(item), 0);
  };

  // Format price for display
  const formatPrice = (price) => {
    const normalized = Number(price);
    if (Number.isNaN(normalized)) return '₹0.00';
    return `₹${normalized.toFixed(2)}`;
  };

  // Handle quantity increase
  const handleIncreaseQuantity = (index) => {
    setCartItems(prev => {
      const updated = [...prev];
      updated[index] = {
        ...updated[index],
        quantity: updated[index].quantity + 1
      };
      // Notify parent of cart update
      if (onCartUpdate) {
        const cartItemsMap = convertToCartMap(updated);
        onCartUpdate(cartItemsMap);
      }
      return updated;
    });
  };

  // Handle quantity decrease
  const handleDecreaseQuantity = (index) => {
    setCartItems(prev => {
      const updated = [...prev];
      if (updated[index].quantity > 1) {
        updated[index] = {
          ...updated[index],
          quantity: updated[index].quantity - 1
        };
      } else {
        // Remove item if quantity becomes 0
        updated.splice(index, 1);
      }
      // Notify parent of cart update
      if (onCartUpdate) {
        const cartItemsMap = convertToCartMap(updated);
        onCartUpdate(cartItemsMap);
      }
      return updated;
    });
  };

  // Convert cart items array to map format
  const convertToCartMap = (items) => {
    const cartItemsMap = {};
    items.forEach((item) => {
      const productId = item.productId || item.product.product_id || item.product.id ||
        `${item.product.product_name}_${item.product.routine_type || 'unknown'}`;
      cartItemsMap[productId] = {
        product: item.product,
        quantity: item.quantity
      };
    });
    return cartItemsMap;
  };

  const buildCheckoutUrlFromCart = (items) => {
    if (!Array.isArray(items) || items.length === 0) return null;

    const variantPairs = [];
    let base = null;

    items.forEach((item) => {
      const raw = item.product || {};
      const qty = Math.max(1, Number(item.quantity) || 1);
      const variant = raw.variant_id || raw.variant || raw.product_variant || raw.id || raw.product_id || raw.sku;
      if (variant != null && String(variant).trim()) {
        variantPairs.push(`${String(variant).trim()}:${qty}`);
      }
      if (!base) {
        const cand = raw.checkout_url || raw.organization_domain_address;
        if (typeof cand === 'string' && cand.trim()) {
          base = cand.trim().replace(/\/$/, '');
        }
      }
    });

    if (base) {
      try {
        const parsed = new URL(base);
        if (parsed.protocol && parsed.host) {
          base = `${parsed.protocol}//${parsed.host}/cart`;
        }
      } catch {
        base = base.replace(/\/$/, '');
      }
    }

    if (base && variantPairs.length > 0) {
      return `${base}/${variantPairs.join(',')}`;
    }

    for (const item of items) {
      const raw = item.product || {};
      const cand = raw.checkout_url || raw.pdp_url || raw.product_link || raw.organization_domain_address;
      if (typeof cand === 'string' && cand.trim()) {
        return cand.trim();
      }
    }
    return null;
  };

  const openCheckoutUrl = (url) => {
    if (!url || checkoutOpenedRef.current) return false;
    try {
      const popup = window.open(url, '_blank', 'noopener,noreferrer');
      if (popup && !popup.closed) {
        checkoutOpenedRef.current = true;
        return true;
      }
      return false;
    } catch {
      return false;
    }
  };

  const persistPlacedOrderTracking = async ({
    baseUrl,
    placedOrderProducts,
    normalizedPhone,
    now,
    localIsoWithOffset,
    offsetMinutes,
    cartSnapshot,
    grandTotal,
  }) => {
    if (trackingSubmittedRef.current) return;
    if (!sessionId || !baseUrl || !Array.isArray(cartSnapshot) || cartSnapshot.length === 0) return;
    trackingSubmittedRef.current = true;

    const response = await fetchWithTimeout(`${baseUrl}/api/save-placed-order-products`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      keepalive: true,
      body: JSON.stringify({
        skin_id: sessionId,
        session_id: sessionId,
        ...(token && { app_session_token: token }),
        placed_order_products: placedOrderProducts,
        customer_name: customer.name.trim(),
        phone_number: normalizedPhone,
        email: customer.email.trim() || null,
        order_status: 'placed',
        payment_details: null,
        placed_at_local: localIsoWithOffset,
        placed_timezone:
          (typeof Intl !== 'undefined' && Intl.DateTimeFormat().resolvedOptions().timeZone) || 'UTC',
        placed_timezone_offset_minutes: offsetMinutes,
        placed_at_epoch_ms: now.getTime(),
        offer_shown: true,
        offer_accepted: true,
        screen_time_ms: Math.max(0, Date.now() - modalOpenedAtRef.current),
        scroll_depth: 0,
        total_amount: Number(grandTotal),
      }),
      timeoutMs: 20000,
    }).catch(() => null);

    if (!response) return;

    const placedResponseJson = await response.json().catch(() => ({}));
    console.info('save-placed-order-products response:', placedResponseJson);
    if (!response.ok) {
      console.warn('Could not save placed order for tracking:', placedResponseJson);
      return;
    }

    if (placedResponseJson?.success) {
      const orderId = placedResponseJson?.order?.id || placedResponseJson?.order_id || placedResponseJson?.id || null;
      const recordBody = {
        skin_id: sessionId,
        session_id: sessionId,
        stage: 'order_placed',
        payload: {
          order_id: orderId,
          total_amount: Number(grandTotal),
          item_count: cartSnapshot.length,
          placed_response: placedResponseJson || null,
        },
        event_time: new Date().toISOString(),
      };
      if (token) recordBody.app_session_token = token;
      fetchWithTimeout(`${baseUrl}/api/record-flow-stage`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        keepalive: true,
        body: JSON.stringify(recordBody),
        timeoutMs: 5000,
      }).catch(() => {});
    }
  };

  const handlePlaceOrder = () => {
    if (placeOrderLockRef.current || isSubmitting) return;

    const normalizedPhone = customer.phone.replace(/\D/g, '');
    if (!customer.name.trim()) {
      alert('Please enter your name before placing the order.');
      return;
    }
    if (normalizedPhone.length < 7 || normalizedPhone.length > 15) {
      alert('Please enter a valid phone number before placing the order.');
      return;
    }
    if (customer.email && !/^\S+@\S+\.\S+$/.test(customer.email.trim())) {
      alert('Please enter a valid email address.');
      return;
    }
    if (!cartItems.length) {
      alert('Your cart is empty.');
      return;
    }

    const checkoutUrl = buildCheckoutUrlFromCart(cartItems);
    if (!checkoutUrl) {
      alert('Unable to open checkout. Product checkout link is missing.');
      return;
    }

    placeOrderLockRef.current = true;
    setIsSubmitting(true);

    const cartSnapshot = cartItems.map((item) => ({ ...item }));
    const grandTotal = cartSnapshot.reduce(
      (total, item) => total + getUnitPriceNumber(item.product) * item.quantity,
      0,
    );

    try {
      const openedInNewTab = openCheckoutUrl(checkoutUrl);

      const now = new Date();
      const pad2 = (value) => String(value).padStart(2, '0');
      const offsetMinutes = -now.getTimezoneOffset();
      const offsetSign = offsetMinutes >= 0 ? '+' : '-';
      const offsetAbs = Math.abs(offsetMinutes);
      const offsetHour = pad2(Math.floor(offsetAbs / 60));
      const offsetMin = pad2(offsetAbs % 60);
      const localIsoWithOffset =
        `${now.getFullYear()}-${pad2(now.getMonth() + 1)}-${pad2(now.getDate())}` +
        `T${pad2(now.getHours())}:${pad2(now.getMinutes())}:${pad2(now.getSeconds())}` +
        `${offsetSign}${offsetHour}:${offsetMin}`;

      const baseUrl = (apiBaseUrl || (typeof window !== 'undefined' && (window.NYAFAZ_API_URL || window.REACT_APP_API_URL || window.location?.origin)) || '').replace(/\/$/, '');
      const placedOrderProducts = cartSnapshot.map((item) => {
        const raw = item.product || {};
        const variantId = raw.variant_id || raw.variant || raw.product_variant || raw.id || raw.product_id || raw.sku || null;
        const checkout = (raw.checkout_url || raw.pdp_url || '') || '';
        return {
          product: {
            variant_id: variantId,
            checkout_url: checkout,
            id: raw.id || raw.product_id || raw.sku || null,
            product_name: raw.product_name || raw.name || '',
          },
          quantity: item.quantity,
        };
      });

      void persistPlacedOrderTracking({
        baseUrl,
        placedOrderProducts,
        normalizedPhone,
        now,
        localIsoWithOffset,
        offsetMinutes,
        cartSnapshot,
        grandTotal,
      }).catch((err) => {
        console.error('Place order tracking error:', err);
      });

      resetLocalFormState();
      onClose?.();

      if (!openedInNewTab) {
        checkoutOpenedRef.current = true;
        window.location.assign(checkoutUrl);
      }
    } catch (err) {
      console.error('Place order error:', err);
      placeOrderLockRef.current = false;
      checkoutOpenedRef.current = false;
      trackingSubmittedRef.current = false;
      setIsSubmitting(false);
      alert('Unable to start checkout. Please try again.');
    }
  };

  const handleClose = () => {
    if (onCartUpdate) {
      onCartUpdate(convertToCartMap(cartItems));
    }
    resetLocalFormState();
    onClose?.();
  };

  if (!isOpen) return null;

  return (
    <div className="checkout-modal-overlay" onClick={handleClose}>
      <div className="checkout-modal-container" onClick={(e) => e.stopPropagation()}>
        <div className="checkout-modal-header">
          <h2 className="checkout-modal-title">Checkout</h2>
          <button
            type="button"
            className="checkout-modal-close-button"
            onClick={handleClose}
            aria-label="Close checkout"
          >
            ×
          </button>
        </div>

        <div className="checkout-modal-content">
          {cartItems.length === 0 ? (
            /* Empty Cart Message */
            <div className="checkout-empty-cart">
              <div className="checkout-empty-cart-content">
                <div className="checkout-empty-cart-icon">
                  <svg
                    width="64"
                    height="64"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <circle cx="9" cy="21" r="1"></circle>
                    <circle cx="20" cy="21" r="1"></circle>
                    <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"></path>
                  </svg>
                </div>
                <h3 className="checkout-empty-cart-title">No products in your cart</h3>
                <p className="checkout-empty-cart-message">Add products to continue shopping</p>
              </div>
            </div>
          ) : (
            <>
              <div className="checkout-modal-customer-section">
                <h3 className="checkout-modal-section-title">Contact Details</h3>
                <div className="checkout-modal-customer-grid">
                  <label className="checkout-modal-field">
                    <span>Name</span>
                    <input
                      type="text"
                      value={customer.name}
                      onChange={(e) => setCustomer((prev) => ({ ...prev, name: e.target.value }))}
                      placeholder="Enter your full name"
                      maxLength={120}
                    />
                  </label>
                  <label className="checkout-modal-field">
                    <span>Phone</span>
                    <input
                      type="tel"
                      value={customer.phone}
                      onChange={(e) => setCustomer((prev) => ({ ...prev, phone: e.target.value }))}
                      placeholder="Enter phone number"
                    />
                  </label>
                  <label className="checkout-modal-field checkout-modal-field-full">
                    <span>Email (optional)</span>
                    <input
                      type="email"
                      value={customer.email}
                      onChange={(e) => setCustomer((prev) => ({ ...prev, email: e.target.value }))}
                      placeholder="Enter email address"
                    />
                  </label>
                </div>
              </div>

              {/* Cart Items List */}
              <div className="checkout-modal-items-section">
                <h3 className="checkout-modal-section-title">Order Summary</h3>
                <div className="checkout-modal-items-list">
                  {cartItems.map((item, index) => {
                    const itemTotal = getItemTotal(item);
                    return (
                      <div key={index} className="checkout-modal-item-card">
                        <div className="checkout-modal-item-image">
                          {item.product.image_url ? (
                            <img
                              src={item.product.image_url}
                              alt={item.product.product_name}
                              onError={(e) => {
                                e.target.style.display = 'none';
                              }}
                            />
                          ) : (
                            <div className="checkout-modal-item-image-placeholder">
                              <span>No Image</span>
                            </div>
                          )}
                        </div>
                        <div className="checkout-modal-item-details">
                          <h4 className="checkout-modal-item-name">{item.product.product_name}</h4>
                          {item.product.description && (
                            <p className="checkout-modal-item-description">{stripHtml(item.product.description).replace(/\s+/g, ' ').trim()}</p>
                          )}
                          <div className="checkout-modal-item-meta">
                                    <div className="checkout-modal-item-price">
                                      <span className="checkout-modal-meta-label">Unit Price:</span>
                                      <span className="checkout-modal-meta-value">
                                        {formatPrice(getUnitPriceNumber(item.product))}
                                        {(item.product && (item.product.sale_price || item.product.discount) && (item.product.amount || item.product.price || item.product.mrp)) ? (
                                          <span className="product-original-price">{formatPrice(_toNumberPrice(item.product.amount ?? item.product.price ?? item.product.mrp))}</span>
                                        ) : null}
                                      </span>
                                    </div>
                            <div className="checkout-modal-item-quantity-controls">
                              <span className="checkout-modal-meta-label">Quantity:</span>
                              <div className="checkout-modal-quantity-counter">
                                <button
                                  type="button"
                                  className="checkout-modal-quantity-button checkout-modal-quantity-decrease"
                                  onClick={() => handleDecreaseQuantity(index)}
                                  aria-label="Decrease quantity"
                                >
                                  −
                                </button>
                                <span className="checkout-modal-quantity-value">{item.quantity}</span>
                                <button
                                  type="button"
                                  className="checkout-modal-quantity-button checkout-modal-quantity-increase"
                                  onClick={() => handleIncreaseQuantity(index)}
                                  aria-label="Increase quantity"
                                >
                                  +
                                </button>
                              </div>
                              </div>
                          </div>
                        </div>
                        <div className="checkout-modal-item-total">
                          <span className="checkout-modal-item-total-label">Item Total</span>
                          <span className="checkout-modal-item-total-value">{formatPrice(itemTotal)}</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Order Summary Sidebar */}
              <div className="checkout-modal-summary-section">
                <div className="checkout-modal-summary-card">
                  <h3 className="checkout-modal-summary-title">Order Total</h3>
                  <div className="checkout-modal-summary-details">
                    <div className="checkout-modal-summary-row">
                      <span className="checkout-modal-summary-label">Items ({cartItems.length})</span>
                      <span className="checkout-modal-summary-value">
                        {formatPrice(cartItems.reduce((sum, item) => sum + getUnitPriceNumber(item.product) * item.quantity, 0))}
                      </span>
                    </div>
                    <div className="checkout-modal-summary-divider"></div>
                    <div className="checkout-modal-summary-row checkout-modal-summary-total">
                      <span className="checkout-modal-summary-label">Total</span>
                      <span className="checkout-modal-summary-value">{formatPrice(getGrandTotal())}</span>
                    </div>
                  </div>
                  <button
                    type="button"
                    className="skin-action-button checkout-modal-place-order-button"
                    onClick={handlePlaceOrder}
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? 'Placing Order...' : 'Place Order'}
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default CheckoutModal;

